import { Toaster } from "ui-widgets";
import { Theme } from "@mui/material/styles";
import { UUID } from "datalign-module-template/models/uuid";
import { Order } from "datalign-module-template/models/order";
import ResponseMessage from "datalign-module-template/models/response-message";

export const capitalizeFirst = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};
export const isUUID = (uuid?: string) =>
  !!uuid &&
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
    uuid,
  );

export const isValidID = (value?: string): number | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const num = Number(trimmed);
  return num > 0 && Number.isInteger(num) ? num : null;
};

export const parseValidNumber = (value?: string): number | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const num = Number(trimmed);
  return !isNaN(num) && isFinite(num) ? num : null;
};

export const reverseDateFormat = (date: string) => {
  const dateArray = String(date).split("/");
  return `${dateArray[2]}/${dateArray[1]}/${dateArray[0]}`;
};

export const handleErrorMessage = (
  error: { message: string },
  defaultMessage?: string,
) => {
  console.error(error);
  Toaster.makeToast(
    error?.message || defaultMessage || "Something went wrong!",
    "error",
    "indef",
  );
};

export const generateGuid = (): UUID => {
  return `xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx`.replace(/[xy]/g, (c) =>
    (c === `x`
      ? (Math.random() * 16) | 0
      : (((Math.random() * 16) | 0) & 0x3) | 0x8
    ).toString(16),
  ) as UUID;
};

export const filterArrayOfUniqueObjects = <T extends object>(
  arrayOfObjects: T[],
  uniqueColumn: keyof T,
) =>
  arrayOfObjects.filter(
    (value, index, array) =>
      array.findIndex((v) => v[uniqueColumn] === value[uniqueColumn]) === index,
  );

export const updateToasterMessage = (
  res: ResponseMessage | string | any,
  defaultMessage?: string,
) => {
  if (!res || !res.message) return defaultMessage;
  return typeof res !== "string"
    ? res?.status
      ? res.status === 200
        ? Toaster.makeToast(res.message, "success")
        : Toaster.makeToast(res.message, "warning")
      : Toaster.makeToast(res.toString(), "error")
    : Toaster.makeToast(res.toString(), "error");
};

export const randomColour = () =>
  `#${(0x1000000 + Math.random() * 0xffffff).toString(16).substring(1, 7)}`;
// export const randomColour = (opacity = 0) => `#${(0x1000000 + Math.random() * 0xffffff).toString(16).substring(1, 7)}${percentToHexRound(opacity) ?? "FF"}`;
const randomByte = () => Math.floor(Math.random() * (255 - 0 + 1));
export const percentToHexRound = (p: number) =>
  `0${Math.round((255 / 100) * p).toString(16)}`.slice(-2).toUpperCase();
export const randomCssRgba = (opacity?: number) =>
  `rgba(${[randomByte(), randomByte(), randomByte(), opacity ?? 100].join(",")})`;
export const getThemeColours = (theme: Theme, index?: number) =>
  index === 0
    ? theme.palette.primary.main
    : index === 1
      ? theme.palette.secondary.main
      : index === 2
        ? theme.palette.info.main
        : index === 3
          ? theme.palette.success.main
          : index === 4
            ? theme.palette.warning.main
            : index === 5
              ? theme.palette.error.main
              : randomColour();

export const tryParseJSONObject = (jsonString: string) => {
  try {
    const o = JSON.parse(jsonString);
    if (o && typeof o === "object") {
      return o;
    }
  } catch (e) {
    console.error(e);
  }
  return false;
};

export const escapeSpecialCharacters = (unescapedText: string) => {
  return unescapedText
    .replace(/[\\]/g, "\\\\")
    .replace(/[/]/g, "\\/")
    .replace(/[\b]/g, "\\b")
    .replace(/[\f]/g, "\\f")
    .replace(/[\n]/g, "\\n")
    .replace(/[\r]/g, "\\r")
    .replace(/[\t]/g, "\\t")
    .replace(/["]/g, '\\"')
    .replace(/\\'/g, "\\'");
};

export const datalignSnakeCase = (machine_name: string) =>
  machine_name.toLowerCase().replaceAll("/[^a-z0-9_]+/", "_");

export function descendingComparator<T>(a: T, b: T, orderBy: keyof T): number {
  const aCompare =
    typeof a[orderBy] === "string"
      ? (a[orderBy] as string).toLowerCase()
      : a[orderBy];
  const bCompare =
    typeof b[orderBy] === "string"
      ? (b[orderBy] as string).toLowerCase()
      : b[orderBy];

  if (bCompare === undefined || bCompare === null) {
    if (aCompare !== undefined && aCompare !== null) {
      return -1;
    }
    return 0;
  }
  if (aCompare === undefined || aCompare === null) {
    if (bCompare !== undefined && bCompare !== null) {
      return 1;
    }
    return 0;
  }

  if (bCompare < aCompare) {
    return -1;
  }
  if (bCompare > aCompare) {
    return 1;
  }
  return 0;
}

export function getComparator<T, Key extends keyof T>(
  order: Order,
  orderBy: Key,
): (a: T, b: T) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}
